import type { TransferActionsProps } from './index.types';
import { useSoftphoneStore } from 'Modules/softphone/store';
import React from 'react';
import { Button } from '~/lib/ui/button';
import { FollowCursorTooltip } from '~/lib/ui/follow-cursor-tooltip';
import { cn } from '~/lib/utils';
import { getTransferOptionDisabled } from './getTransferOptionDisabled';
import { getTransferOptionHelperText } from './getTransferOptionHelperText';
import { TRANSFER_OPTIONS } from './index.constants';

/**
 * TransferActions - Transfer type selection and execution controls
 */
const TransferActions = ({
  onTransfer,
  testID,
}: TransferActionsProps): React.ReactElement => {
  const {
    transferType: selectedType,
    isTransferring,
    setTransferType,
    transferTarget,
    transferSelectedPhoneNumber,
  } = useSoftphoneStore((state) => ({
    transferType: state.transferType,
    isTransferring: state.isTransferring,
    setTransferType: state.setTransferType,
    transferTarget: state.transferTarget,
    transferSelectedPhoneNumber: state.transferSelectedPhoneNumber,
  }));

  const componentTestId = testID
    ? `${testID}-transfer-actions`
    : 'transfer-actions';

  return (
    <div
      className="flex flex-col gap-[32px] mx-[16px] mt-[16px] mb-[32px]"
      data-testid={componentTestId}
    >
      <fieldset className="flex flex-col gap-[16px]">
        <legend className="sr-only">Transfer type</legend>

        {TRANSFER_OPTIONS.map((option) => {
          const isDisabled = getTransferOptionDisabled(
            option.value,
            transferTarget,
            transferSelectedPhoneNumber
          );

          const helperText = isDisabled
            ? getTransferOptionHelperText(
                option.value,
                transferTarget,
                transferSelectedPhoneNumber
              )
            : null;

          return (
            <FollowCursorTooltip
              key={option.value}
              tooltipInfo={{ show: !!helperText, message: helperText ?? '' }}
            >
              {({ onMouseEnter, onMouseLeave }) => (
                <label
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  className={cn(
                    'flex items-center gap-[8px] cursor-pointer rounded-[4px] p-[4px] transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary has-[:focus-visible]:ring-offset-2',
                    isDisabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <input
                    type="radio"
                    name="transfer-type"
                    value={option.value}
                    checked={selectedType === option.value}
                    onChange={() => setTransferType(option.value)}
                    disabled={isDisabled}
                    className="accent-primary h-[16px] w-[16px] flex-shrink-0 focus:outline-none"
                    data-testid={`${componentTestId}-${option.value}`}
                  />
                  <div className="flex flex-col gap-[2px]">
                    {option?.label && (
                      <span className="text-[14px] leading-[19px] font-normal text-foreground">
                        {option?.label}
                      </span>
                    )}
                    {option?.description && (
                      <span className="text-[12px] leading-[16px] font-normal text-muted-foreground">
                        {option?.description}
                      </span>
                    )}
                  </div>
                </label>
              )}
            </FollowCursorTooltip>
          );
        })}
      </fieldset>

      <div className="flex justify-center">
        <Button
          variant="default"
          onClick={onTransfer}
          disabled={isTransferring}
          className="px-[24px] py-[8px] h-[40px] rounded-[4px] bg-primary hover:bg-primary-hover text-background font-semibold text-[14px]"
          data-testid={`${componentTestId}-submit`}
          aria-label={isTransferring ? 'Transfer in progress' : 'Transfer call'}
          aria-busy={isTransferring}
        >
          {isTransferring ? 'Transferring...' : 'Transfer'}
        </Button>
      </div>
    </div>
  );
};

TransferActions.displayName = 'TransferActions';

export { TransferActions };
